import React, { FC, useEffect, useState } from 'react';
import { SimpleOrderContext } from './simple-order-context';
import SimpleOrder from './simple-order.component';
import { useParams } from 'react-router-dom';
import { useLazyQuery } from '@apollo/client';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { formatPinNumber } from 'utils/personal-identity-number';
import { Box, CircularProgress } from '@mui/material';
import { Order } from 'models/order';
import { orderForSimpleModifyTemplate } from 'apollo/templates/order/query.graphql';
import { restorationAsInput } from 'components/order-flow/shared/restoration-input-helper';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { permissions } from 'utils/permission';
import { SimpleOrderInput } from 'models/order-input/simple-order';
import { extractOrderInputType } from 'utils/orders';

const ModifySimpleOrder: FC = () => {
    const [order, setOrder] = useState<SimpleOrderInput>({} as SimpleOrderInput);
    const params = useParams<{ id: string }>();
    const { showBoundary } = useDenthubErrorBoundary();
    const currentOrg = currentOrgVar();

    const [fetchOrder, { loading }] = useLazyQuery(orderForSimpleModifyTemplate, {
        fetchPolicy: 'no-cache',
        onCompleted: ({ order }: { order: Order }) => {
            if (order) {
                if (order.type !== 'SIMPLE_TYPE') {
                    throw new Error('Order is not a simple order');
                }
                setOrder({
                    inputType: extractOrderInputType(order),
                    isWhiteLabeled: permissions.isWhiteLabeledOrganization(currentOrg),
                    _id: order._id,
                    supplier: order.supplier,
                    customer: order.customer,
                    dentist: order.dentist,
                    // DD-3494: a patient-less order prefills no patient, as a fresh wizard starts.
                    patient: order.patient
                        ? {
                              personalIdentityNumber: formatPinNumber(order.patient.personalIdentityNumber),
                              firstName: order.patient.firstName,
                              lastName: order.patient.lastName,
                              noPersonalIdentityNumber: order.patient.noPersonalIdentityNumber || false,
                          }
                        : undefined,
                    restorations: (order.restorations ?? []).map(restorationAsInput),
                    comment: '',
                    historicalComments: order.historicalComments || [],
                    participants: {
                        linkedDentists: order.linkedDentists || [],
                        linkedClinics: order.linkedClinics || [],
                        customParticipantEmails: order.customParticipantEmails || [],
                    },
                    files: order.filesUrl || [],
                    sample: order.deliveryDetails.sample || { type: '', content: [], otherContent: '' },
                    deliveryDetails: {
                        date: order.deliveryDetails.date ? new Date(order.deliveryDetails.date) : null,
                        time: {
                            hour: order.deliveryDetails.time?.hour,
                            minute: order.deliveryDetails.time?.minute,
                        },
                        pickUpLocation: order.deliveryDetails.pickUpLocation,
                        destination: order.deliveryDetails.destination,
                    },
                } as SimpleOrderInput);
            }
        },
        onError(error) {
            showBoundary(error);
        },
    });

    useEffect(() => {
        fetchOrder({ variables: { orderId: params.id } });
    }, [params.id]);

    return (
        <SimpleOrderContext.Provider value={{ order, setOrder }}>
            {loading || !order._id ? (
                <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
                    <Box>
                        <CircularProgress size={80} />
                    </Box>
                </Box>
            ) : (
                <SimpleOrder />
            )}
        </SimpleOrderContext.Provider>
    );
};

export default ModifySimpleOrder;
